import { useEffect, useId, useRef, useState } from "react";
import type { PdfPasswordReason } from "@/pdf";
import { useModalFocus } from "@/shared/use-modal-focus";
import { usePresence } from "@/shared/use-presence";

type Props = {
  request: {
    reason: PdfPasswordReason;
    fileName: string;
    attempt: number;
  } | null;
  onSubmit(password: string): void;
  onCancel(): void;
};

export function PasswordDialog({ request, onSubmit, onCancel }: Props) {
  const titleId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const presence = usePresence(Boolean(request), 240);
  const [lastRequest, setLastRequest] = useState(request);
  const visibleRequest = request ?? lastRequest;
  const dialogRef = useModalFocus<HTMLFormElement>(
    Boolean(request),
    onCancel,
    inputRef,
  );

  useEffect(() => {
    if (!request) return;
    const frame = requestAnimationFrame(() => {
      setLastRequest(request);
      if (inputRef.current) {
        inputRef.current.value = "";
        inputRef.current.focus();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [request]);

  if (!presence.present || !visibleRequest) return null;

  return (
    <div
      className="dialog-backdrop"
      data-motion-state={presence.state}
      aria-hidden={!request}
      role="presentation"
    >
      <form
        ref={dialogRef}
        className="dialog-card password-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onSubmit={(event) => {
          event.preventDefault();
          const password = inputRef.current?.value ?? "";
          if (password) onSubmit(password);
        }}
      >
        <div className="dialog-heading">
          <div>
            <p className="eyebrow">
              {visibleRequest.reason === "incorrect-password"
                ? "Try again"
                : "Protected PDF"}
            </p>
            <h2 id={titleId}>
              {visibleRequest.reason === "incorrect-password"
                ? "That password did not work."
                : "Enter the PDF password."}
            </h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label="Cancel opening protected PDF"
            onClick={onCancel}
          >
            ×
          </button>
        </div>
        <p className="password-file-name">{visibleRequest.fileName}</p>
        <label className="password-field">
          <span>Password</span>
          <input
            ref={inputRef}
            type="password"
            autoComplete="off"
            defaultValue=""
          />
        </label>
        <p className="privacy-line">
          The password is passed directly to PDF.js and is never stored.
        </p>
        <div className="dialog-actions">
          <button className="text-button" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="primary-button"
            type="submit"
          >
            Unlock PDF
          </button>
        </div>
      </form>
    </div>
  );
}
